<template>
  <div :class="[wrapperClasses, wrapperClass]" :style="wrapperStyles">
    <div :class="[bemm('container'), containerClass]">
      <div v-if="hasMeta" :class="[bemm('meta'), metaClass]">
        <label
          v-if="hasLabel"
          ref="labelContainerRef"
          :for="id || undefined"
          :class="[bemm('label'), labelClass]"
          :data-ellipsis-title="showLabelTooltip && label ? label : undefined"
        >
          <TooltipWrapper :text="label" :disabled="!showLabelTooltip" stretch>
            <span ref="labelContentRef" :class="bemm('label-content')">
              <span ref="labelTextRef" :class="[bemm('label-text'), labelTextClass]">
                {{ label }}
              </span>
              <Icon
                v-if="required"
                :name="Icons.ASTERISK"
                :class="[bemm('required'), requiredClass]"
                aria-hidden="true"
              />
            </span>
          </TooltipWrapper>
          <slot name="label-after" />
        </label>
        <TooltipWrapper
          v-if="hasDescription"
          :text="description"
          :disabled="!isDescriptionEllipsed"
          stretch
        >
          <p
            ref="descriptionRef"
            :class="[bemm('description'), descriptionClass]"
            :data-ellipsis-title="
              isDescriptionEllipsed && description ? description : undefined
            "
          >
            {{ description }}
          </p>
        </TooltipWrapper>
        <div
          v-else-if="hasDescriptionSlot"
          :class="[bemm('description'), descriptionClass]"
        >
          <slot name="description" />
        </div>
      </div>
      <div
        :id="controlId || undefined"
        :class="[bemm('control', [fit ? 'fit' : '']), controlClass]"
      >
        <slot />
      </div>
    </div>
    <p v-if="error" :class="[bemm('error'), errorClass]">{{ error }}</p>
  </div>
</template>

<script lang="ts" setup>
import { computed, getCurrentInstance, PropType, useSlots } from "vue";
import { useBemm } from "bemm";
import { Icon, Icons } from "../Icon";
import { TooltipWrapper } from "../Tooltip";
import { useEllipsisDetection } from "../composables";

type ClassValue = string | string[] | Record<string, boolean>;

const props = defineProps({
  id: {
    type: String,
    default: null,
  },
  label: {
    type: String,
    default: "",
  },
  description: {
    type: String,
    default: "",
  },
  hideLabel: {
    type: Boolean,
    default: false,
  },
  hideMeta: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: "",
  },
  error: {
    type: String,
    default: "",
  },
  direction: {
    type: String as PropType<"auto" | "row" | "column">,
    default: "auto",
  },
  fit: {
    type: Boolean,
    default: false,
  },
  spaceBetween: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: false,
  },
  wrapperClass: {
    type: [String, Array, Object] as PropType<ClassValue>,
    default: "",
  },
  containerClass: {
    type: [String, Array, Object] as PropType<ClassValue>,
    default: "",
  },
  metaClass: {
    type: [String, Array, Object] as PropType<ClassValue>,
    default: "",
  },
  labelClass: {
    type: [String, Array, Object] as PropType<ClassValue>,
    default: "",
  },
  labelTextClass: {
    type: [String, Array, Object] as PropType<ClassValue>,
    default: "",
  },
  requiredClass: {
    type: [String, Array, Object] as PropType<ClassValue>,
    default: "",
  },
  descriptionClass: {
    type: [String, Array, Object] as PropType<ClassValue>,
    default: "",
  },
  controlClass: {
    type: [String, Array, Object] as PropType<ClassValue>,
    default: "",
  },
  controlId: {
    type: String,
    default: null,
  },
  errorClass: {
    type: [String, Array, Object] as PropType<ClassValue>,
    default: "",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const {
  elementRef: labelTextRef,
  isEllipsed: isLabelTextEllipsed,
} = useEllipsisDetection(() => props.label);
const {
  elementRef: labelContentRef,
  isEllipsed: isLabelContentEllipsed,
} = useEllipsisDetection(() => props.label);
const {
  elementRef: labelContainerRef,
  isEllipsed: isLabelContainerEllipsed,
} = useEllipsisDetection(() => props.label);
const {
  elementRef: descriptionRef,
  isEllipsed: isDescriptionEllipsed,
} = useEllipsisDetection(() => props.description);

const instance = getCurrentInstance();
const slots = useSlots();

const hasExplicitLabelProp = computed(() => {
  const vnodeProps = instance?.vnode.props;
  if (!vnodeProps) {
    return false;
  }

  return (
    Object.prototype.hasOwnProperty.call(vnodeProps, "label") &&
    vnodeProps.label != null &&
    vnodeProps.label !== ""
  );
});

const hasLabel = computed(() => {
  if (props.hideLabel) {
    return false;
  }

  return hasExplicitLabelProp.value || Boolean(String(props.label || "").trim());
});
const hasDescription = computed(() => Boolean(String(props.description || "").trim()));
const hasDescriptionSlot = computed(() => Boolean(slots.description));
const hasMeta = computed(() => !props.hideMeta);
const showLabelTooltip = computed(() =>
  Boolean(
    props.label &&
      (isLabelTextEllipsed.value ||
        isLabelContentEllipsed.value ||
        isLabelContainerEllipsed.value)
  )
);

const wrapperClasses = computed(() => [
  "ar-field",
  props.direction !== "auto" ? `ar-field--${props.direction}` : "",
  props.status ? `ar-field--${props.status}` : "",
  { "ar-field--error": Boolean(props.error) },
  { "ar-field--disabled": props.disabled },
  { "ar-field--space-between": props.spaceBetween },
]);

const wrapperStyles = computed(() => {
  const styles: Record<string, string> = {};
  if (props.direction && props.direction !== "auto") {
    styles["--form-direction"] = props.direction;
  }
  if (props.fit) {
    styles["--field-min-width"] = "0px";
    styles["--field-width"] = "fit-content";
    styles["container-type"] = "normal";
  }
  return styles;
});

const bemm = useBemm("ar-field", {
  return: "string",
  includeBaseClass: true,
});
</script>

<style lang="scss">
.ar-field {
  $b: &;
  --int-field-min-height: calc(var(--space) * 2.625);
  display: flex;
  // Always stacks the container above the error message. The label/control
  // arrangement inside #{$b}__container is the only thing --form-direction
  // should drive (via the --row modifier and the container query below) —
  // reusing it here too used to also flip this outer flex to row, which
  // made #{$b}__container shrink-wrap instead of filling the field's width.
  flex-direction: column;
  gap: var(--space-xs);
  width: var(--field-width, 100%);
  min-width: var(--field-min-width, 140px);
  container-type: inline-size;

  &__input {
    color: currentColor;
  }

  &__label {
    font-weight: var(--font-weight-medium);
    margin: 0;
    position: relative;
    white-space: nowrap;
    display: flex;
    min-width: 0;

    &[data-ellipsis-title] {
      cursor: help;
    }
  }

  &__label-content {
    display: flex;
    align-items: center;
    min-width: 0;
    width: 100%;
  }

  &__label-text {
    display: block;
    min-width: 0;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &__required {
    color: var(--color-error);
    margin-inline-start: var(--space-xs);
    font-weight: var(--font-weight-bold);
    font-size: var(--font-size-xs);
  }

  &__description {
    margin: 0;
    font-size: var(--font-size-s);
    color: color-mix(in srgb, var(--color-foreground), transparent 35%);
    line-height: 1.4;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;

    &[data-ellipsis-title] {
      cursor: help;
    }
  }

  &__meta {
    display: flex;
    flex-direction: column;
    gap: calc(var(--space-xs) * 0.5);
  }

  &__control {
    display: flex;
    align-items: center;
    justify-content: var(--field-justify-content, flex-start);
    min-height: var(--field-min-height, var(--int-field-min-height));
    gap: var(--space-xs);
    position: relative;
    width: var(--field-control-width, auto);
    font-size: 1em;

    &--fit {
      width: fit-content;
    }
  }

  &__container {
    display: flex;
    flex-direction: column;
    gap: var(--space-xs);
  }

  &--row {
    #{$b}__container {
      flex-direction: row;
      align-items: center;
    }
  }

  &--space-between {
    #{$b}__container {
      justify-content: space-between;
    }
    #{$b}__control {
      width: auto;
    }
  }

  &__error {
    color: var(--color-error);
    font-size: var(--font-size-s);
  }

  &--success {
    .ar-field__control {
      border-color: var(--color-success);
    }
  }

  &--warning {
    .ar-field__control {
      border-color: var(--color-warning);
    }
  }

  &--error {
    .ar-field__control {
      border-color: var(--color-error);
    }
  }

  &--disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  @container (width > 400px) {
    #{$b}__container {
      flex-direction: var(--form-direction, row);
      align-items: center;
    }
    #{$b}__control {
      width: 100%;
      &--fit {
        width: fit-content;
      }
    }
    #{$b}__meta {
      padding: var(--space-xs) 0;
      width: var(--input-label-width, max(30%, 150px));
      height: auto;
    }
    &#{$b}--column {
      #{$b}__meta {
        width: 100%;
      }
    }
  }
}
</style>
